<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Edit Category</span>
        </v-card-title>
        <v-card-text>
          <div v-if="isLoading" class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <p class="mt-2">Loading category...</p>
          </div>
          <div v-else-if="!category && !isLoading" class="text-center">
            <v-icon large color="error">mdi-alert-circle</v-icon>
            <p class="mt-2">Category not found</p>
            <v-btn color="primary" @click="$router.back()">Go Back</v-btn>
          </div>
          <div v-else>
            <v-row>
              <v-col sm="6">
                <v-text-field
                  v-model="form.name"
                  solo
                  rounded
                  label="Category Name *"
                  prepend-inner-icon="mdi-tag"
                  :error-messages="errors.name"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-switch
                  class="mt-0 ml-5"
                  v-model="form.is_active"
                  inset
                  :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                ></v-switch>
              </v-col>
              <v-col sm="12">
                <v-textarea
                  v-model="form.description"
                  solo
                  rounded
                  label="Description"
                  rows="4"
                  prepend-inner-icon="mdi-text"
                  :error-messages="errors.description"
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Category Information Card -->
            <v-card class="mt-4" outlined>
              <v-card-title>
                <v-icon left>mdi-information</v-icon>
                Category Information
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col sm="6">
                    <strong>Created:</strong><br />
                    {{ formatDate(category.created_at) }}
                  </v-col>
                  <v-col sm="6">
                    <strong>Last Updated:</strong><br />
                    {{ formatDate(category.updated_at) }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider class="mt-5 mb-5"></v-divider>
            <v-row>
              <v-col sm="12">
                <v-btn
                  :loading="isSaving"
                  :disabled="isSaving"
                  color="primary"
                  @click="updateCategory"
                  class="mr-4"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Update Category
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="$router.back()"
                  :disabled="isSaving"
                  class="mr-4"
                >
                  <v-icon left>mdi-cancel</v-icon>
                  Cancel
                </v-btn>
                <v-btn
                  color="error"
                  @click="confirmDelete"
                  :disabled="isSaving"
                  outlined
                >
                  <v-icon left>mdi-delete</v-icon>
                  Delete Category
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Category updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-alert-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Confirm Delete</h1>
                      <br />
                      <h3>Are you sure you want to delete this category?</h3>
                      <br />
                      <v-btn
                        color="error"
                        @click="deleteCategory"
                        :loading="isDeleting"
                        class="mr-4"
                      >
                        Yes, Delete
                      </v-btn>
                      <v-btn
                        color="secondary"
                        @click="showDeleteDialog = false"
                        :disabled="isDeleting"
                      >
                        Cancel
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CategoryEditPage",
  data() {
    return {
      category: null,
      form: {
        name: "",
        description: "",
        is_active: true,
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showSuccessDialog: false,
      showDeleteDialog: false,
    };
  },
  mounted() {
    this.fetchCategory();
  },
  methods: {
    async fetchCategory() {
      this.isLoading = true;
      try {
        const categoryId = this.$route.params.id;
        const response = await axios.get(`/categories/${categoryId}`);

        if (response.data.success) {
          this.category = response.data.data;
          this.form = {
            name: this.category.name,
            description: this.category.description || "",
            is_active: this.category.is_active,
          };
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Category not found",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Category not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading category data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async updateCategory() {
      this.isSaving = true;
      this.errors = {};

      try {
        const categoryData = {
          name: this.form.name,
          description: this.form.description,
          is_active: this.form.is_active,
        };

        const categoryId = this.$route.params.id;
        const response = await axios.patch(
          `/categories/${categoryId}`,
          categoryData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.category = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the category.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while updating the category.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deleteCategory() {
      this.isDeleting = true;

      try {
        const categoryId = this.$route.params.id;
        const response = await axios.post(`/categories/delete/${categoryId}`);

        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push("/categories");
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while deleting the category.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the category.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },

  watch: {
    // Clear errors when user starts typing
    "form.name"() {
      if (this.errors.name) {
        this.$delete(this.errors, "name");
      }
    },
    "form.description"() {
      if (this.errors.description) {
        this.$delete(this.errors, "description");
      }
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}

.v-text-field.solo .v-input__control {
  min-height: 48px;
}

.v-textarea.solo .v-input__control {
  min-height: 120px;
}
</style>